import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * String Helpers - Migrados de devyx-ui
 * Funções utilitárias para manipulação de strings
 */

/**
 * Codifica objeto em query string para URL
 */
export function encodeToQueryParams(params: Record<string, any>): string {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value)) {
        value.forEach(item => searchParams.append(key, String(item)));
      } else if (typeof value === 'object') {
        searchParams.append(key, JSON.stringify(value));
      } else {
        searchParams.append(key, String(value));
      }
    }
  });

  return searchParams.toString();
}

/**
 * Remove máscara de telefone
 * Ex: (11) 98765-4321 → 11987654321
 */
export function removePhoneMask(phone: string): string {
  return phone?.replace(/\D/g, '') || '';
}

/**
 * Insere máscara de telefone
 * Ex: 11987654321 → (11) 98765-4321
 * Ex: 1134567890 → (11) 3456-7890
 */
export function insertPhoneMask(phone: string): string {
  const cleaned = phone?.replace(/\D/g, '') || '';

  if (cleaned.length === 11) {
    // Mobile: (XX) 9XXXX-XXXX
    const match = cleaned.match(/^(\d{2})(\d{5})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
  } else if (cleaned.length === 10) {
    // Landline: (XX) XXXX-XXXX
    const match = cleaned.match(/^(\d{2})(\d{4})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
  }

  return phone;
}

/**
 * Remove máscara de CEP
 * Ex: 12345-678 → 12345678
 */
export function removeCEPmask(cep: string): string {
  return cep?.replace(/\D/g, '') || '';
}

/**
 * Remove máscara de documento (CPF/CNPJ)
 * Ex: 123.456.789-00 → 12345678900
 * Ex: 12.345.678/0001-99 → 12345678000199
 */
export function removeDocumentMask(document: string): string {
  return document?.replace(/\D/g, '') || '';
}

/**
 * Insere máscara de documento (CPF ou CNPJ)
 * Ex: 12345678900 → 123.456.789-00 (CPF)
 * Ex: 12345678000199 → 12.345.678/0001-99 (CNPJ)
 */
export function insertDocumentMask(document: string): string {
  const cleaned = document?.replace(/\D/g, '') || '';

  if (cleaned.length === 11) {
    // CPF: XXX.XXX.XXX-XX
    return formatCPF(cleaned);
  } else if (cleaned.length === 14) {
    // CNPJ: XX.XXX.XXX/XXXX-XX
    return formatCNPJ(cleaned);
  }

  return document;
}

export function insertCEPmask(cep: string): string {
  const cleaned = cep?.replace(/\D/g, '') || '';
  const match = cleaned.match(/^(\d{5})(\d{3})$/);

  if (match) {
    return `${match[1]}-${match[2]}`;
  }

  return cep;
}

/**
 * Formata valor monetário em BRL
 */
export function formatCurrency(value: number | string): string {
  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(numValue)) return 'R$ 0,00';

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(numValue);
}

/**
 * Formata número no padrão brasileiro
 * Ex: 1234.56 → 1.234,56
 * Ex: 1234 → 1.234
 *
 * @param value - Número a ser formatado
 * @param decimals - Número de casas decimais (padrão: 0 para inteiros, automático para decimais)
 */
export function formatNumber(
  value: number | string,
  decimals?: number,
): string {
  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(numValue)) return '0';

  // Se decimals não for especificado, detecta se é inteiro ou decimal
  const hasDecimals =
    decimals !== undefined ? decimals : numValue % 1 !== 0 ? 2 : 0;

  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: hasDecimals,
    maximumFractionDigits: hasDecimals,
  }).format(numValue);
}

/**
 * Capitaliza primeira letra de cada palavra
 * Ex: "hello world" → "Hello World"
 */
export function formatFirstLetter(text: string): string {
  if (!text) return '';

  return text
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Formata CPF
 * Ex: 12345678900 → 123.456.789-00
 */
export function formatCPF(cpf: string): string {
  const cleaned = cpf?.replace(/\D/g, '') || '';
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{3})(\d{2})$/);

  if (match) {
    return `${match[1]}.${match[2]}.${match[3]}-${match[4]}`;
  }

  return cpf;
}

/**
 * Formata CNPJ
 * Ex: 12345678000199 → 12.345.678/0001-99
 */
export function formatCNPJ(cnpj: string): string {
  const cleaned = cnpj?.replace(/\D/g, '') || '';
  const match = cleaned.match(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/);

  if (match) {
    return `${match[1]}.${match[2]}.${match[3]}/${match[4]}-${match[5]}`;
  }

  return cnpj;
}

/**
 * Valida CPF
 */
export function validatesCPF(cpf: string): boolean {
  const cleaned = cpf?.replace(/\D/g, '') || '';

  if (cleaned.length !== 11 || /^(\d)\1{10}$/.test(cleaned)) {
    return false;
  }

  let sum = 0;
  let remainder;

  for (let i = 1; i <= 9; i++) {
    sum += parseInt(cleaned.substring(i - 1, i)) * (11 - i);
  }

  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cleaned.substring(9, 10))) return false;

  sum = 0;
  for (let i = 1; i <= 10; i++) {
    sum += parseInt(cleaned.substring(i - 1, i)) * (12 - i);
  }

  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cleaned.substring(10, 11))) return false;

  return true;
}

/**
 * Valida CNPJ
 */
export function validatesCNPJ(cnpj: string): boolean {
  const cleaned = cnpj?.replace(/\D/g, '') || '';

  if (cleaned.length !== 14 || /^(\d)\1{13}$/.test(cleaned)) {
    return false;
  }

  let length = cleaned.length - 2;
  let numbers = cleaned.substring(0, length);
  const digits = cleaned.substring(length);
  let sum = 0;
  let pos = length - 7;

  for (let i = length; i >= 1; i--) {
    sum += parseInt(numbers.charAt(length - i)) * pos--;
    if (pos < 2) pos = 9;
  }

  let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(digits.charAt(0))) return false;

  length = length + 1;
  numbers = cleaned.substring(0, length);
  sum = 0;
  pos = length - 7;

  for (let i = length; i >= 1; i--) {
    sum += parseInt(numbers.charAt(length - i)) * pos--;
    if (pos < 2) pos = 9;
  }

  result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(digits.charAt(1))) return false;

  return true;
}
